import type { ApolloCache } from '@apollo/client';
import { gql } from '@apollo/client';

import type { TargetingTermValue } from '../../models';
import {
  computeHasInvalidCountryForAdSave,
  computeHasInvalidPublisherForAdSave,
  getAllCountryCodesMentionedInAdTargeting,
  getCountriesTargetFromAdTargetingSubmission,
  getDisplayPublisherTargetAfterAdTargetingSave,
} from './targetingTermValueSelectors';

/**
 * Fields on an `AdV5` entity that are derived server-side by CampaignSearcher from the ad's
 * targeting term values combined with the parent line item's constraints. Because CampaignSearcher
 * is an async consumer, an ad mutation response (or a refetch issued immediately after) may return
 * stale values for these fields. This fragment captures the canonical set of fields that callers
 * should refresh locally after a targeting-affecting save so the UI stays in sync.
 */
export const AD_TARGETING_EMBELLISHMENT_FIELDS = gql`
  fragment AdTargetingEmbellishmentFields on AdV5 {
    id
    hasInvalidPublisher
    hasInvalidCountry
    displayPublisherTarget
    countriesTarget
  }
`;

export type AdTargetingEmbellishmentFields = {
  __typename: 'AdV5';
  id: string;
  hasInvalidPublisher: boolean;
  hasInvalidCountry: boolean;
  displayPublisherTarget: string[];
  countriesTarget: string[];
};

type Args = {
  cache: ApolloCache<unknown>;
  adId: string;
  /** The ad's targeting term values (as submitted to / returned from the mutation). */
  submittedAdTermValues: TargetingTermValue[];
  /** The parent line item's `displayPublisherTarget`. */
  lineItemDisplayPublisherTarget: string[];
  /** The parent line item's `countries`. */
  lineItemCountries: string[] | undefined;
  /**
   * Optional fallback for `displayPublisherTarget` when the ad has no publisher rows AND the line
   * item's display publisher list is empty (e.g. not loaded yet). Typically the ad's current value
   * from cache, to avoid wiping a good CampaignSearcher-derived list when only patching flags.
   */
  fallbackAdDisplayPublisherTarget?: string[];
};

/**
 * Compute the four targeting-derived "embellishment" fields for an ad from the just-saved term
 * values and the parent line item's constraints, and write them into the Apollo cache via
 * `writeFragment`. Safe to call from either a mutation `update` callback or a post-success handler.
 *
 * Returns the fields that were written (useful for tests and debugging); returns `null` if the
 * cache does not normalize the ad (e.g. missing `__typename`/`id` in the store).
 */
export function writeAdTargetingEmbellishmentFieldsToCache({
  cache,
  adId,
  submittedAdTermValues,
  lineItemDisplayPublisherTarget,
  lineItemCountries,
  fallbackAdDisplayPublisherTarget,
}: Args): AdTargetingEmbellishmentFields | null {
  const cacheId = cache.identify({ __typename: 'AdV5', id: adId });
  if (!cacheId) return null;

  const displayPublisherTarget = getDisplayPublisherTargetAfterAdTargetingSave(
    submittedAdTermValues,
    lineItemDisplayPublisherTarget,
    fallbackAdDisplayPublisherTarget
  );
  const countriesTarget = getCountriesTargetFromAdTargetingSubmission(submittedAdTermValues, lineItemCountries);
  const hasInvalidPublisher = computeHasInvalidPublisherForAdSave(
    submittedAdTermValues,
    lineItemDisplayPublisherTarget
  );
  const hasInvalidCountry = computeHasInvalidCountryForAdSave(
    getAllCountryCodesMentionedInAdTargeting(submittedAdTermValues),
    lineItemCountries
  );

  const data: AdTargetingEmbellishmentFields = {
    __typename: 'AdV5',
    id: adId,
    hasInvalidPublisher,
    hasInvalidCountry,
    displayPublisherTarget,
    countriesTarget,
  };

  cache.writeFragment<AdTargetingEmbellishmentFields>({
    id: cacheId,
    fragment: AD_TARGETING_EMBELLISHMENT_FIELDS,
    data,
  });

  return data;
}
